import { isString, has } from 'lodash';

import ModalInstance from 'commons-general/services/ModalService/ModalInstance.js';

/**
 * Service handling the outsourced state of the modal components
 *
 * @class ModalService
 * @since 1.6.0 (probably below)
 */
class ModalService {
    /**
     * Creates an instance of ModalService.
     * @memberof ModalService
     * @constructor
     */
    constructor() {
        /**
         * @memberof ModalService
         */
        this.instances = {};
    }

    /**
     * Get the instance of {@link ModalInstance} matching the given name.
     * If it does not exist, create it.
     *
     * @memberof ModalService
     * @param {String} name string naming an instance
     * @returns {ModalInstance} an instance of {@link ModalInstance} matching the given name.
     */
    get(name) {
        if (!isString(name)) {
            throw new TypeError(`ModalService#get: "name" should be a 'string' (${typeof name})`);
        }

        // instantiate ModalInstance if not found in list
        if (!this.has(name)) {
            this.instantiate(name);
        }

        return this.instances[name];
    }

    /**
     * Verify if an instance of of {@link ModalInstance} is already instantiated
     *
     * @memberof ModalService
     * @param {String} name The instance name
     * @returns {Boolean}
     */
    has(name) {
        if (!isString(name)) {
            throw new TypeError(`ModalService#has: "name" should be a 'string' (${typeof name})`);
        }

        return has(this.instances, name);
    }

    /**
     * Delete the instance of {@link ModalInstance} matching the given name, from the registered list.
     *
     * @memberof ModalService
     * @param {String} name string naming an instance
     * @returns {Boolean} whether the instance of {@link ModalInstance} matching the given name has been deleted or not.
     */
    remove(name) {
        try {
            if (!isString(name)) {
                return false;
            }

            // ignore if not found in list
            if (!has(this.instances, name)) {
                return false;
            }

            this.instances[name] = null;
            return delete this.instances[name];
        } catch (e) {
            return false;
        }
    }

    /**
     * Instanciate a new instance of {@link ModalInstance}, registered with the given name.
     *
     * @memberof ModalService
     * @param {String} name string naming a new instance
     * @returns {ModalInstance} an instance of {@link ModalInstance}.
     * @throws {TypeError} when the 'name' argument is not a string, or already used.
     */
    instantiate(name) {
        const prefix = 'ModalService#instantiate:';

        if (!isString(name)) {
            throw new TypeError(`${prefix} "name" should be a "string" (${typeof name})`);
        }

        if (has(this.instances, name)) {
            throw new TypeError(`${prefix} "name" '${name}' is already in use`);
        }

        this.instances[name] = new ModalInstance();
        return this.instances[name];
    }
}

export default new ModalService();
